import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  ArrowLeft, Plus, RefreshCw, AlertTriangle, CheckCircle2, Clock, 
  MessageSquare, Calendar, Building2, FileText, Send, X, Filter,
  AlertCircle, TrendingUp
} from 'lucide-react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';

const PRIORITY_COLORS = {
  low: 'bg-green-100 text-green-800 border-green-300',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  critical: 'bg-red-100 text-red-800 border-red-300'
};

const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  pending_review: 'bg-purple-100 text-purple-800',
  closed: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-800'
};

const GCCorrectiveActions = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState([]);
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProject, setFilterProject] = useState('all');
  
  const [newAction, setNewAction] = useState({
    projectId: '',
    subCompanyId: '',
    title: '',
    description: '',
    priority: 'medium',
    dueDate: ''
  });
  
  const [projectSubs, setProjectSubs] = useState([]);
  const [closingNotes, setClosingNotes] = useState('');

  useEffect(() => {
    fetchData();
  }, [filterStatus, filterProject]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterProject !== 'all') params.append('project_id', filterProject);
      
      const [actionsRes, statsRes, projectsRes] = await Promise.all([
        axiosInstance.get(`/gc/corrective-actions?${params.toString()}`),
        axiosInstance.get('/gc/corrective-actions/stats'),
        axiosInstance.get('/gc/projects')
      ]);
      
      setActions(actionsRes.data);
      setStats(statsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load corrective actions');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectSubs = async (projectId) => {
    if (!projectId) {
      setProjectSubs([]);
      return;
    }
    try {
      const response = await axiosInstance.get(`/gc/projects/${projectId}/subcontractors`);
      setProjectSubs(response.data.filter(s => s.status === 'active'));
    } catch (error) {
      console.error('Error fetching subs:', error);
      setProjectSubs([]);
    }
  };

  const handleCreateAction = async () => {
    if (!newAction.projectId || !newAction.subCompanyId || !newAction.title || !newAction.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await axiosInstance.post('/gc/corrective-actions', newAction);
      toast.success('Corrective action created');
      setShowCreateDialog(false);
      setNewAction({ projectId: '', subCompanyId: '', title: '', description: '', priority: 'medium', dueDate: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create corrective action');
    }
  };

  const handleCloseAction = async () => {
    if (!selectedAction) return;
    
    try {
      await axiosInstance.put(`/gc/corrective-actions/${selectedAction.id}/close?resolution_notes=${encodeURIComponent(closingNotes)}`);
      toast.success('Corrective action closed');
      setShowDetailsDialog(false);
      setSelectedAction(null);
      setClosingNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close action');
    }
  };

  const openDetails = async (action) => {
    try {
      const response = await axiosInstance.get(`/gc/corrective-actions/${action.id}`);
      setSelectedAction(response.data);
      setShowDetailsDialog(true);
    } catch (error) {
      toast.error('Failed to load action details');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isOverdue = (action) => {
    if (!action.dueDate || action.status === 'closed') return false;
    return new Date(action.dueDate) < new Date();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/gc/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Corrective Actions</h1>
              <p className="text-gray-600">Track and manage safety corrective actions with subcontractors</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              New Action
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card>
              <CardContent className="p-4 text-center">
                <FileText className="w-6 h-6 text-blue-500 mx-auto mb-1" />
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-gray-500">Total Actions</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <AlertCircle className="w-6 h-6 text-blue-500 mx-auto mb-1" />
                <p className="text-2xl font-bold">{stats.byStatus?.open || 0}</p>
                <p className="text-xs text-gray-500">Open</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Clock className="w-6 h-6 text-yellow-500 mx-auto mb-1" />
                <p className="text-2xl font-bold">{stats.byStatus?.in_progress || 0}</p>
                <p className="text-xs text-gray-500">In Progress</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <AlertTriangle className="w-6 h-6 text-red-500 mx-auto mb-1" />
                <p className="text-2xl font-bold">{stats.overdue}</p>
                <p className="text-xs text-gray-500">Overdue</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <CheckCircle2 className="w-6 h-6 text-green-500 mx-auto mb-1" />
                <p className="text-2xl font-bold">{stats.byStatus?.closed || 0}</p>
                <p className="text-xs text-gray-500">Closed</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Filter className="w-4 h-4 text-gray-500" />
              <div className="flex gap-4">
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Status:</Label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="pending_review">Pending Review</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Project:</Label>
                  <Select value={filterProject} onValueChange={setFilterProject}>
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Projects</SelectItem>
                      {projects.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions List */}
        <Card>
          <CardHeader>
            <CardTitle>Corrective Actions</CardTitle>
            <CardDescription>Click on an action to view details and responses</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : actions.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-600">No Corrective Actions</h3>
                <p className="text-gray-500 mb-4">Create a corrective action to track safety issues with subcontractors</p>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Action
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Subcontractor</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Responses</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {actions.map((action) => (
                    <TableRow 
                      key={action.id} 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => openDetails(action)}
                    >
                      <TableCell className="font-medium">{action.title}</TableCell>
                      <TableCell>{action.projectName}</TableCell>
                      <TableCell>{action.subCompanyName}</TableCell>
                      <TableCell>
                        <Badge className={PRIORITY_COLORS[action.priority]}>
                          {action.priority}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={isOverdue(action) ? STATUS_COLORS.overdue : STATUS_COLORS[action.status]}>
                          {isOverdue(action) ? 'Overdue' : action.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className={isOverdue(action) ? 'text-red-600 font-medium' : ''}>
                        {formatDate(action.dueDate)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <MessageSquare className="w-4 h-4 text-gray-400" />
                          <span>{action.responses?.length || 0}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create Action Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Corrective Action</DialogTitle>
              <DialogDescription>Request a corrective action from a subcontractor</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Project *</Label>
                <Select 
                  value={newAction.projectId} 
                  onValueChange={(v) => {
                    setNewAction({ ...newAction, projectId: v, subCompanyId: '' });
                    fetchProjectSubs(v);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project..." />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Subcontractor *</Label>
                <Select 
                  value={newAction.subCompanyId} 
                  onValueChange={(v) => setNewAction({ ...newAction, subCompanyId: v })}
                  disabled={!newAction.projectId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={newAction.projectId ? "Select subcontractor..." : "Select project first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {projectSubs.map(s => (
                      <SelectItem key={s.subCompanyId} value={s.subCompanyId}>{s.subCompanyName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={newAction.title}
                  onChange={(e) => setNewAction({ ...newAction, title: e.target.value })}
                  placeholder="e.g., PPE Compliance Issue"
                />
              </div>
              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={newAction.description}
                  onChange={(e) => setNewAction({ ...newAction, description: e.target.value })}
                  placeholder="Describe the issue and required corrective action..."
                  rows={4}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select 
                    value={newAction.priority} 
                    onValueChange={(v) => setNewAction({ ...newAction, priority: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={newAction.dueDate}
                    onChange={(e) => setNewAction({ ...newAction, dueDate: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateAction}>Create Action</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Action Details Dialog */}
        <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            {selectedAction && (
              <>
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <DialogTitle>{selectedAction.title}</DialogTitle>
                      <DialogDescription>
                        Created {formatDate(selectedAction.createdAt)}
                      </DialogDescription>
                    </div>
                    <div className="flex gap-2">
                      <Badge className={PRIORITY_COLORS[selectedAction.priority]}>
                        {selectedAction.priority}
                      </Badge>
                      <Badge className={isOverdue(selectedAction) ? STATUS_COLORS.overdue : STATUS_COLORS[selectedAction.status]}>
                        {isOverdue(selectedAction) ? 'Overdue' : selectedAction.status.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  {/* Details */}
                  <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-500">Project</p>
                      <p className="font-medium">{selectedAction.projectName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Subcontractor</p>
                      <p className="font-medium">{selectedAction.subCompanyName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Due Date</p>
                      <p className={`font-medium ${isOverdue(selectedAction) ? 'text-red-600' : ''}`}>
                        {formatDate(selectedAction.dueDate)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      <p className="font-medium capitalize">{selectedAction.status.replace('_', ' ')}</p>
                    </div>
                  </div>
                  
                  {/* Description */}
                  <div>
                    <h4 className="font-medium mb-2">Description</h4>
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedAction.description}</p>
                  </div>
                  
                  {/* Responses */}
                  <div>
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Responses ({selectedAction.responses?.length || 0})
                    </h4>
                    {selectedAction.responses?.length > 0 ? (
                      <div className="space-y-3">
                        {selectedAction.responses.map((response, idx) => (
                          <div key={response.id || idx} className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-sm">{response.respondedByName}</span>
                              <span className="text-xs text-gray-500">{formatDate(response.respondedAt)}</span>
                            </div>
                            <p className="text-gray-700">{response.response}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm italic">No responses yet</p>
                    )}
                  </div>
                  
                  {/* Close Action */}
                  {selectedAction.status !== 'closed' && (
                    <div className="pt-4 border-t">
                      <h4 className="font-medium mb-2">Close This Action</h4>
                      <Textarea
                        value={closingNotes}
                        onChange={(e) => setClosingNotes(e.target.value)}
                        placeholder="Add resolution notes (optional)..."
                        rows={2}
                        className="mb-3"
                      />
                      <Button onClick={handleCloseAction} className="bg-green-600 hover:bg-green-700">
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Close Action
                      </Button>
                    </div>
                  )}
                  
                  {/* Resolution Notes */}
                  {selectedAction.status === 'closed' && selectedAction.resolutionNotes && (
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <h4 className="font-medium text-green-800 mb-1">Resolution Notes</h4>
                      <p className="text-green-700">{selectedAction.resolutionNotes}</p>
                      <p className="text-xs text-green-600 mt-2">Closed on {formatDate(selectedAction.closedAt)}</p>
                    </div>
                  )}
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>Close</Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default GCCorrectiveActions;
